/**
 * Masks and gradients inside letters, on the canvas.
 *
 * The press walk (press/draw.ts) says these with clips and soft masks; this is
 * the same geometry for the screen. The outline comes from the same shape
 * library, the same drawing paths and the same text placement, and the soft
 * edge and fade from the same sums in effects/mask.ts - so a frame, a feather
 * or a fade looks on screen as it prints.
 *
 * Everything is in millimetres: the caller has scaled the context so one unit
 * is one millimetre of the layer.
 */

import type { Gradient, ImageLayer, Mask } from '../model/types';
import { toCss } from '../model/units';
import { graphicPaths } from '../press/draw';
import { shapePath } from '../shapes/library';
import { placeText, type Matrix } from '../text/place';
import { layoutFor } from '../text/layout';
import { fadeLine, fades, fadeStart, featherMm, frameMatrix, IDENTITY, multiply, unflip, boxOutline } from '../effects/mask';
import { pathFor } from './paint';

export interface CanvasMask {
  /** The whole outline as one path, in the picture's millimetres. */
  path: Path2D;
  evenOdd: boolean;
  feather: number;
  fade: Mask[ 'fade' ];
}

/**
 * A mask layer's outline, in its own frame. Null while its font is loading.
 */
export function outlineOf( layer: NonNullable<Mask[ 'layer' ]> ): Array<{ path: Path2D; m: Matrix }> | null {
  if ( layer.type === 'shape' ) return [ { path: pathFor( shapePath( layer ) ), m: IDENTITY } ];

  if ( layer.type === 'graphic' ) return graphicPaths( layer ).map( ( p ) => ( { path: pathFor( p.d ), m: IDENTITY } ) );

  const laid = layoutFor( layer );

  if ( ! laid ) return null;

  const placed = placeText( layer, laid );
  const out = placed.glyphs.map( ( g ) => ( { path: pathFor( g.d ), m: g.m } ) );

  for ( const rule of placed.rules ) {
    const path = new Path2D();

    path.rect( rule.x, rule.y, rule.w, rule.h );
    out.push( { path, m: IDENTITY } );
  }

  return out;
}

/** Many outlines as one path, each put in place. */
export function combine( outlines: Array<{ path: Path2D; m: Matrix }>, frame: Matrix = IDENTITY ): Path2D {
  const path = new Path2D();

  for ( const outline of outlines ) {
    const [ a, b, c, d, e, f ] = multiply( frame, outline.m );

    path.addPath( outline.path, new DOMMatrix( [ a, b, c, d, e, f ] ) );
  }

  return path;
}

/**
 * A picture's mask, ready to draw with. Null when it has none worth drawing,
 * or when its outline is still waiting for a font.
 */
export function canvasMask( layer: ImageLayer ): CanvasMask | null | 'waiting' {
  const mask = layer.mask;

  if ( ! mask ) return null;

  const feather = featherMm( mask, layer.w, layer.h );
  const fade = fades( mask.fade ) ? mask.fade : null;

  if ( ! mask.layer || mask.layer.hidden ) {
    if ( feather <= 0 && ! fade ) return null;

    const box = boxOutline( layer.w, layer.h, feather );

    return { path: pathFor( box.d ), evenOdd: false, feather, fade };
  }

  const outlines = outlineOf( mask.layer );

  if ( ! outlines ) return 'waiting';

  return {
    path: combine( outlines, multiply( unflip( layer ), frameMatrix( mask.layer ) ) ),
    evenOdd: mask.layer.type === 'graphic' && mask.layer.paths.length > 0 && mask.layer.paths.every( ( p ) => p.evenOdd ),
    feather,
    fade,
  };
}

/**
 * Draws something through a mask.
 *
 * A hard mask is a clip. A soft one is drawn into a canvas of its own - the
 * picture, then the mask's alpha over it with destination-in - and that is laid
 * onto the card. The softness is the browser's shadow blur, which works in
 * every browser, where a canvas filter does not.
 */
export function drawMasked(
  ctx: CanvasRenderingContext2D,
  mask: CanvasMask,
  box: { w: number; h: number },
  draw: ( ctx: CanvasRenderingContext2D ) => void
): void {
  const soft = mask.feather > 0 || !! mask.fade;

  if ( ! soft ) {
    ctx.save();
    ctx.clip( mask.path, mask.evenOdd ? 'evenodd' : 'nonzero' );
    draw( ctx );
    ctx.restore();

    return;
  }

  // Pixels per millimetre on screen, capped so a zoomed-in poster stays cheap.
  const t = ctx.getTransform();
  const scale = Math.min( Math.hypot( t.a, t.b ) || 1, 4096 / Math.max( box.w, box.h, 1 ) );
  const width = Math.max( 1, Math.ceil( box.w * scale ) );
  const height = Math.max( 1, Math.ceil( box.h * scale ) );

  const off = document.createElement( 'canvas' );

  off.width = width;
  off.height = height;

  const o = off.getContext( '2d' );

  if ( ! o ) return;

  o.scale( width / box.w, height / box.h );
  draw( o );

  o.globalCompositeOperation = 'destination-in';

  if ( mask.feather > 0 ) {
    // The outline far away, its white shadow landing in place.
    const away = width + height + mask.feather * scale * 8 + 100;
    const m = document.createElement( 'canvas' );

    m.width = width;
    m.height = height;

    const mc = m.getContext( '2d' );

    if ( ! mc ) return;

    mc.setTransform( width / box.w, 0, 0, height / box.h, -away, 0 );
    mc.shadowColor = '#fff';
    mc.shadowBlur = 2 * mask.feather * scale;
    mc.shadowOffsetX = away;
    mc.fillStyle = '#fff';
    mc.fill( mask.path, mask.evenOdd ? 'evenodd' : 'nonzero' );

    o.setTransform( 1, 0, 0, 1, 0, 0 );
    o.drawImage( m, 0, 0 );
    o.setTransform( width / box.w, 0, 0, height / box.h, 0, 0 );
  } else {
    o.fillStyle = '#fff';
    o.fill( mask.path, mask.evenOdd ? 'evenodd' : 'nonzero' );
  }

  if ( mask.fade ) {
    o.save();
    o.transform( box.w, 0, 0, box.h, 0, 0 );

    const start = fadeStart( mask.fade );
    let gradient: CanvasGradient;

    if ( mask.fade.kind === 'radial' ) {
      gradient = o.createRadialGradient( 0.5, 0.5, 0, 0.5, 0.5, 0.5 );
    } else {
      const line = fadeLine( mask.fade );

      gradient = o.createLinearGradient( line.x1, line.y1, line.x2, line.y2 );
    }

    gradient.addColorStop( 0, 'rgba(255,255,255,1)' );
    gradient.addColorStop( start, 'rgba(255,255,255,1)' );
    gradient.addColorStop( 1, 'rgba(255,255,255,0)' );

    o.fillStyle = gradient;
    o.fillRect( 0, 0, 1, 1 );
    o.restore();
  }

  ctx.drawImage( off, 0, 0, box.w, box.h );
}

/**
 * A gradient across a box, the way SVG's objectBoundingBox paints it - so the
 * angle and the ellipse of a radial match the proof and the press file.
 */
export function fillGradientBox( ctx: CanvasRenderingContext2D, fill: Gradient, box: { x: number; y: number; w: number; h: number } ): void {
  ctx.save();
  ctx.transform( box.w, 0, 0, box.h, box.x, box.y );

  let gradient: CanvasGradient;

  if ( fill.kind === 'radial' ) {
    gradient = ctx.createRadialGradient( 0.5, 0.5, 0, 0.5, 0.5, 0.5 );
  } else {
    const a = ( fill.angle * Math.PI ) / 180;
    const dx = Math.cos( a ) / 2;
    const dy = Math.sin( a ) / 2;

    gradient = ctx.createLinearGradient( 0.5 - dx, 0.5 - dy, 0.5 + dx, 0.5 + dy );
  }

  for ( const stop of fill.stops ) {
    gradient.addColorStop( Math.min( 1, Math.max( 0, stop.at ) ), toCss( stop.colour ) );
  }

  ctx.fillStyle = gradient;
  ctx.fillRect( 0, 0, 1, 1 );
  ctx.restore();
}

/** Which part of a picture shows in a box, for drawImage: source pixels. */
export function sourceRect(
  picture: { crop: { x: number; y: number; w: number; h: number }; fit: 'cover' | 'contain' | 'fill' },
  box: { w: number; h: number },
  natural: { w: number; h: number }
): { x: number; y: number; w: number; h: number } {
  const region = {
    x: picture.crop.x * natural.w,
    y: picture.crop.y * natural.h,
    w: Math.max( 1, picture.crop.w * natural.w ),
    h: Math.max( 1, picture.crop.h * natural.h ),
  };

  if ( picture.fit === 'fill' || box.w <= 0 || box.h <= 0 ) return region;

  const boxRatio = box.w / box.h;
  const wider = region.w / region.h > boxRatio;

  if ( picture.fit === 'cover' ) {
    if ( wider ) {
      const w = region.h * boxRatio;

      return { ...region, x: region.x + ( region.w - w ) / 2, w };
    }

    const h = region.w / boxRatio;

    return { ...region, y: region.y + ( region.h - h ) / 2, h };
  }

  if ( wider ) {
    const h = region.w / boxRatio;

    return { ...region, y: region.y - ( h - region.h ) / 2, h };
  }

  const w = region.h * boxRatio;

  return { ...region, x: region.x - ( w - region.w ) / 2, w };
}

/** CSS filters approximating the picture's own adjustments, for a masked picture. */
export function filterCss( filters: ImageLayer[ 'filters' ] ): string {
  const parts: string[] = [];

  if ( filters.brightness ) parts.push( `brightness(${ Math.max( 0, 1 + filters.brightness ) })` );
  if ( filters.contrast ) parts.push( `contrast(${ Math.max( 0, 1 + filters.contrast ) })` );
  if ( filters.greyscale ) parts.push( 'grayscale(1)' );
  else if ( filters.saturation ) parts.push( `saturate(${ Math.max( 0, 1 + filters.saturation ) })` );

  return parts.length ? parts.join( ' ' ) : 'none';
}
